import { createSignal, Show, onMount } from 'solid-js'
import { useNavigate, useSearchParams } from '@solidjs/router'
import { useAuth } from '../lib/auth'

export default function Transfer() {
  const navigate = useNavigate()
  const auth = useAuth()
  const [searchParams] = useSearchParams()

  const [status, setStatus] = createSignal<'claiming' | 'success' | 'error'>('claiming')
  const [error, setError] = createSignal<string | null>(null)

  const token = searchParams.token

  onMount(async () => {
    if (!token || typeof token !== 'string' || token.length !== 6 || !/^[A-Z0-9]+$/.test(token)) {
      setError('No transfer code provided.')
      setStatus('error')
      return
    }

    try {
      const res = await fetch(`${import.meta.env.VITE_API_URL || 'https://api.zipcheck.my'}/v1/session-transfer/claim`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      })

      const data = await res.json()

      if (data.token) {
        // Store the session token
        localStorage.setItem('session_token', data.token)
        setStatus('success')

        // Redirect to pass after a short delay
        setTimeout(() => navigate('/pass'), 2000)
      } else {
        setError(data.error?.message || 'Failed to claim transfer.')
        setStatus('error')
      }
    } catch (err) {
      setError('Network error. Please try again.')
      setStatus('error')
    }
  })

  return (
    <div class="min-h-dvh bg-gray-50 flex flex-col">
      <header class="bg-white border-b border-gray-200 px-4 py-3">
        <div class="max-w-md mx-auto flex items-center gap-3">
          <img src="/logo.svg" alt="ZipCheck" class="h-8" />
          <span class="text-lg font-semibold text-gray-900">ZipCheck</span>
        </div>
      </header>

      <main class="flex-1 flex items-center justify-center p-4">
        <div class="w-full max-w-md">
          {/* Claiming */}
          <Show when={status() === 'claiming'}>
            <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6 text-center">
              <div class="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span class="text-3xl">⏳</span>
              </div>
              <h1 class="text-xl font-bold text-gray-900 mb-2">Setting up your account...</h1>
              <p class="text-sm text-gray-600">
                Transferring your ZipCheck account to this device.
              </p>
            </div>
          </Show>

          {/* Success */}
          <Show when={status() === 'success'}>
            <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6 text-center">
              <div class="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span class="text-3xl">✓</span>
              </div>
              <h1 class="text-xl font-bold text-gray-900 mb-2">Account transferred!</h1>
              <p class="text-sm text-gray-600">
                Your ZipCheck account is now active on this device. Redirecting...
              </p>
            </div>
          </Show>

          {/* Error */}
          <Show when={status() === 'error'}>
            <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6 text-center">
              <div class="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span class="text-3xl">✕</span>
              </div>
              <h1 class="text-xl font-bold text-gray-900 mb-2">Transfer failed</h1>
              <p class="text-sm text-gray-600 mb-4">{error()}</p>
              <button
                onClick={() => navigate('/signup')}
                class="py-2 px-4 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
              >
                Try signing up instead
              </button>
            </div>
          </Show>
        </div>
      </main>
    </div>
  )
}
